/**
 * MediaLibraryPanel — the Media Library as a draggable, resizable, collapsible
 * floating panel (mirrors the Add/Format panel shells), hosting a thumbnail grid
 * of the site's assets (derived catalog — mediaCatalog) enriched with the
 * named/versioned index (assetIndex, ydoc-synced). An Expand toggle pops it to a
 * large centered overlay for heavy management.
 *
 * ONE panel, two modes (mediaLibrary store), distinguished only by `pickTarget`:
 * BROWSE (🗂️ toolbar — free kind filter, per-tile Replace/rename/versions/
 * rollback/remove/sweep) and PICK (a widget's "choose from library" — filter
 * locked to the kind, an armed banner + accent ring; selecting binds the asset,
 * then DISARMS and stays open). Web-only chrome; native is a follow-up.
 *
 * react-strict-dom (`html.*` + `css.create`). RSD 0.0.55 forwards neither the
 * drag events (dragover/dragleave/drop/dragstart) nor `title`, so those are
 * attached to the forwarded DOM node in effects (`useNativeListener`,
 * `useNativeTitle`). A tile's Replace is Duro's FileTrigger.
 */
import {
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import { css, html } from "react-strict-dom";
import {
  Badge,
  Button,
  Callout,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  FileTrigger,
  Input,
  Toggle,
  ToggleGroup,
  Tooltip,
  type BadgeVariant,
} from "@duro-app/ui";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import {
  microSpacing,
  radii,
  spacing,
} from "@duro-app/tokens/tokens/spacing.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";
import { shadows } from "@duro-app/tokens/tokens/shadows.css";
import { layers } from "@duro-app/tokens/tokens/layers.css";
import { typeScale, typography } from "@duro-app/tokens/tokens/typography.css";
import { useGridStore } from "@fredericrous/grid";

import { useSiteId } from "./SiteContext.js";
import { useEditorContextOptional } from "./EditorContext.js";
import { useAddDrag } from "./AddDragContext.js";
import { usePanelDock } from "./panelDock.js";
import { DockablePanel } from "./DockablePanel.js";
import {
  useMediaCatalog,
  rewriteImageSha,
  bindAssetToMediaItem,
} from "./assets/mediaCatalog.js";
import {
  getSyncedShas,
  deleteAsset,
  putAsset,
} from "./assets/opfsAssetStore.js";
import { useBackupStatus, markBackingUp } from "./assets/collabBackupStatus.js";
import { useRoomPeerCount } from "./assets/collabPeers.js";
import {
  getCollabBlobAnnounce,
  getCollabHolderProbe,
} from "./assets/collabBlobResolver.js";
import { loadAssetBlob } from "./assets/loadAssetBlob.js";
import {
  useAssetIndex,
  ensureEntry,
  renameAsset,
  pushVersion,
  rollbackTo,
  removeEntry,
  type VersionRef,
} from "./assets/assetIndex.js";
import {
  useMediaLibrary,
  setKindFilter,
  disarmPick,
  type FilterKind,
} from "./mediaLibrary.js";

/** Attach a native DOM listener to an `html.*` element's forwarded node — for
 *  the events RSD does not forward (drag-and-drop). The handler is an effect
 *  event, so it always sees the latest render's state. */
function useNativeListener<K extends keyof HTMLElementEventMap>(
  ref: RefObject<HTMLElement | null>,
  type: K,
  handler: (e: HTMLElementEventMap[K]) => void,
) {
  const onEvent = useEffectEvent(handler);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const listener = (e: HTMLElementEventMap[K]) => onEvent(e);
    el.addEventListener(type, listener);
    return () => el.removeEventListener(type, listener);
  }, [ref, type]);
}

/** The native hover hint (`title`) on an `html.*` element, which RSD does not
 *  forward. Layout boxes only — a control's hint is a Duro Tooltip. */
function useNativeTitle(
  ref: RefObject<HTMLElement | null>,
  title: string | undefined,
) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (title) el.title = title;
    else el.removeAttribute("title");
  }, [ref, title]);
}

type AssetKind = "image" | "video" | "audio";

/** What a tile's Replace picker accepts, by the asset's kind. */
const REPLACE_ACCEPT: Record<AssetKind, string> = {
  image: "image/jpeg,image/png,image/webp,image/gif,image/svg+xml",
  video: "video/mp4,video/webm,video/ogg,video/quicktime",
  audio:
    "audio/mpeg,audio/ogg,audio/wav,audio/webm,audio/mp4,audio/aac,audio/flac",
};

interface DisplayAsset {
  sha256: string;
  kind: AssetKind;
  name: string;
  mime?: string;
  ext?: string;
  refCount: number;
  stored: boolean;
  entryId: string | null;
  history: readonly VersionRef[];
}

/** The Media Library CONTENT (prop-free) — count/sweep row, armed banner, kind
 *  filter, drop-to-upload, and the asset grid. Rendered by the standalone shell
 *  below and (Phase 3) by the docked group; reads `ydoc` from EditorContext so
 *  both hosts get it without prop-drilling. */
export function MediaLibraryBody() {
  const siteId = useSiteId();
  const { pickTarget, kindFilter } = useMediaLibrary();
  const ydoc = useEditorContextOptional()?.ydoc ?? null;
  const { assets, loading, refresh } = useMediaCatalog();
  const index = useAssetIndex(ydoc);
  const [synced, setSynced] = useState<Set<string>>(new Set());
  const [dragOver, setDragOver] = useState(false);
  // Transient "✓ Added" confirmation after a pick binds (mode-error feedback).
  const [boundToast, setBoundToast] = useState(false);
  // Multi-select delete: "Remove N unused" enters selection mode with the unused
  // pre-checked; the user adjusts, hits Delete, and confirms once.
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);

  // Merge the derived (sha) catalog with the named/versioned index: hide history
  // shas (they're versions, shown under their asset), attach names/history, and
  // surface index-only assets (peer-only named assets not local yet).
  const display: DisplayAsset[] = useMemo(() => {
    const hist = new Set<string>(
      index.flatMap((e) => e.history.map((h) => h.sha)),
    );
    const byCur = new Map(index.map((e) => [e.currentSha, e] as const));
    const out = new Map<string, DisplayAsset>();
    for (const a of assets) {
      if (hist.has(a.sha256)) continue;
      const e = byCur.get(a.sha256);
      out.set(a.sha256, {
        sha256: a.sha256,
        kind: a.kind,
        name: e?.name ?? "",
        mime: a.mime ?? e?.mime,
        ext: a.ext ?? e?.ext,
        refCount: a.refCount,
        stored: a.stored,
        entryId: e?.id ?? null,
        history: e?.history ?? [],
      });
    }
    for (const e of index) {
      if (hist.has(e.currentSha) || out.has(e.currentSha)) continue;
      out.set(e.currentSha, {
        sha256: e.currentSha,
        kind: e.mime?.startsWith("video")
          ? "video"
          : e.mime?.startsWith("audio")
            ? "audio"
            : "image",
        name: e.name,
        mime: e.mime,
        ext: e.ext,
        refCount: 0,
        stored: false,
        entryId: e.id,
        history: e.history,
      });
    }
    // One explicit kind filter drives both modes. In pick mode the store locks
    // `kindFilter` to the target's kind, reproducing the old pick behaviour;
    // browse lets the user switch it.
    const list = [...out.values()];
    return kindFilter === "all"
      ? list
      : list.filter((a) => a.kind === kindFilter);
  }, [assets, index, kindFilter]);

  useEffect(() => {
    let cancelled = false;
    if (!siteId) return;
    void getSyncedShas(siteId).then((s) => {
      if (!cancelled) setSynced(s);
    });
    return () => {
      cancelled = true;
    };
  }, [siteId, assets]);

  useEffect(() => {
    // Esc backs out one layer: confirm dialog → selection mode → armed pick.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (confirming) setConfirming(false);
      else if (selectMode) exitSelect();
      else if (pickTarget) disarmPick();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pickTarget, selectMode, confirming]);

  // Highlight the widget being chosen for on the canvas, so the armed panel and
  // its target are visually connected (reuses the grid selection affordance).
  useEffect(() => {
    if (!pickTarget) return;
    useGridStore
      .getState()
      .setSelection(pickTarget.gridId, [pickTarget.itemId]);
  }, [pickTarget]);

  // Briefly confirm a bind, then leave pick mode (panel stays open).
  function confirmBind() {
    setBoundToast(true);
    window.setTimeout(() => setBoundToast(false), 1500);
    disarmPick();
  }

  function pick(sha: string, a: DisplayAsset) {
    if (!pickTarget) return;
    bindAssetToMediaItem(pickTarget.gridId, pickTarget.itemId, {
      sha256: sha,
      mime: a.mime,
      ext: a.ext,
    });
    confirmBind(); // UI-mode effects (disarm + toast) stay here, not in the helper
  }

  // Does a dropped file fit this dialog? In pick mode only the requested kind
  // (image/video/audio); in browse mode any supported media type.
  function fileMatchesKind(f: File): boolean {
    const t = f.type || "";
    if (pickTarget) return t.startsWith((pickTarget.kind ?? "image") + "/");
    return (
      t.startsWith("image/") || t.startsWith("video/") || t.startsWith("audio/")
    );
  }

  // Drop files onto the panel → upload each into the library (content-address +
  // announce for backup). While armed the first accepted file also binds to the
  // target widget (then disarms), so "drop an image while choosing" just works.
  async function handleDroppedFiles(files: FileList) {
    if (!siteId) return;
    const accepted = Array.from(files).filter(fileMatchesKind);
    if (accepted.length === 0) return;
    let bound = false;
    for (const file of accepted) {
      const asset = await putAsset(siteId, file);
      if (ydoc)
        ensureEntry(ydoc, asset.sha256, { mime: asset.mime, ext: asset.ext });
      getCollabBlobAnnounce(siteId)?.(asset.sha256);
      markBackingUp(asset.sha256);
      if (pickTarget && !bound) {
        bound = true;
        const st = useGridStore.getState();
        const cur = st.grids[pickTarget.gridId]?.items[pickTarget.itemId];
        if (cur) {
          const next: Record<string, unknown> = { ...(cur.contentProps ?? {}) };
          next["sha256"] = asset.sha256;
          next["mime"] = asset.mime;
          next["ext"] = asset.ext;
          delete next["url"];
          st.updateItem(pickTarget.gridId, pickTarget.itemId, {
            contentProps: next,
          });
        }
      }
    }
    refresh();
    if (bound) confirmBind();
  }

  function rename(a: DisplayAsset, name: string) {
    if (!ydoc) return;
    const e = ensureEntry(ydoc, a.sha256, { mime: a.mime, ext: a.ext });
    renameAsset(ydoc, e.id, name.trim());
  }

  // Replace the asset EVERYWHERE with a freshly-picked file: rewrite the grids,
  // record a new version, and replicate the new bytes to peers.
  async function onReplaceFile(sha: string, file: File) {
    if (!siteId || !ydoc) return;
    const asset = await putAsset(siteId, file);
    // Same bytes: nothing to rewrite, but they may be stored here only now (a
    // "needs a source" asset given its file back), so the tile must re-read.
    if (asset.sha256 === sha) return refresh();
    rewriteImageSha(sha, asset.sha256, { mime: asset.mime, ext: asset.ext });
    const e = ensureEntry(ydoc, sha);
    pushVersion(ydoc, e.id, asset.sha256, { mime: asset.mime, ext: asset.ext });
    getCollabBlobAnnounce(siteId)?.(asset.sha256);
    markBackingUp(asset.sha256);
    refresh();
  }

  function rollback(a: DisplayAsset, version: VersionRef) {
    if (!ydoc || !a.entryId) return;
    // Rewrite widgets to the version's OWN mime/ext — not the current tile's,
    // which would be wrong after a cross-format replace (png→webp, image→video).
    rewriteImageSha(a.sha256, version.sha, {
      mime: version.mime,
      ext: version.ext,
    });
    rollbackTo(ydoc, a.entryId, version.sha);
    if (siteId) getCollabBlobAnnounce(siteId)?.(version.sha);
    refresh();
  }

  // Fully delete one asset: its named/versioned index entry (+ history bytes)
  // AND the current bytes. Deleting only the OPFS bytes would leave an index-only
  // "needs a source" ghost that keeps reappearing in the catalog.
  async function purgeAsset(a: DisplayAsset) {
    if (!siteId) return;
    if (a.entryId && ydoc) {
      for (const h of a.history) await deleteAsset(siteId, h.sha);
      removeEntry(ydoc, a.entryId);
    }
    await deleteAsset(siteId, a.sha256);
  }
  async function remove(a: DisplayAsset) {
    if (!siteId) return;
    await purgeAsset(a);
    refresh();
  }

  // Enter selection mode with the unused assets pre-checked (the "Remove N
  // unused" entry point). The user can uncheck ones to keep (or check used ones)
  // before deleting — one confirmation, granular control.
  function enterSelect() {
    setSelected(
      new Set(display.filter((a) => a.refCount === 0).map((a) => a.sha256)),
    );
    setSelectMode(true);
  }
  function toggleSelect(sha: string) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(sha)) n.delete(sha);
      else n.add(sha);
      return n;
    });
  }
  function exitSelect() {
    setSelectMode(false);
    setSelected(new Set());
    setConfirming(false);
  }
  async function deleteSelected() {
    if (!siteId) return;
    const byId = new Map(display.map((a) => [a.sha256, a] as const));
    const targets = [...selected]
      .map((sha) => byId.get(sha))
      .filter((a): a is DisplayAsset => a != null);
    exitSelect();
    for (const a of targets) await purgeAsset(a);
    refresh();
  }

  const orphanCount = display.filter((a) => a.refCount === 0).length;
  const storedOrphans = display.filter(
    (a) => a.refCount === 0 && a.stored,
  ).length;

  const armed = !!pickTarget;
  const armedKindLabel =
    pickTarget?.kind === "video"
      ? "a video"
      : pickTarget?.kind === "audio"
        ? "an audio file"
        : "an image";

  // Drop files onto the panel → upload (RSD forwards no drag events, so the
  // body's drag-and-drop listens on its DOM node).
  const rootRef = useRef<HTMLDivElement>(null);
  useNativeListener(rootRef, "dragover", (e) => {
    e.preventDefault();
    if (!dragOver) setDragOver(true);
  });
  useNativeListener(rootRef, "dragleave", (e) => {
    // Ignore bubbling leaves onto children — only clear when the pointer
    // actually left the body.
    const root = e.currentTarget as HTMLElement | null;
    if (!root?.contains(e.relatedTarget as Node | null)) setDragOver(false);
  });
  useNativeListener(rootRef, "drop", (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length)
      void handleDroppedFiles(e.dataTransfer.files);
  });

  return (
    <html.div ref={rootRef} style={s.bodyRoot}>
      {boundToast && (
        <html.div style={s.toast}>
          <Badge variant="success">✓ Added to the block</Badge>
        </html.div>
      )}
      {dragOver && (
        <html.div style={s.dropOverlay}>
          <html.div style={s.dropHint}>
            ⬇︎ Drop to upload
            {pickTarget
              ? pickTarget.kind === "video"
                ? " a video"
                : pickTarget.kind === "audio"
                  ? " an audio file"
                  : " an image"
              : ""}
          </html.div>
        </html.div>
      )}
      {selectMode ? (
        <html.div style={[s.row, s.selectBar]}>
          <html.span style={s.selectedCount}>
            {selected.size} selected
          </html.span>
          <html.div style={s.pushRight}>
            <Button variant="secondary" size="small" onClick={exitSelect}>
              Cancel
            </Button>
            <Tooltip.Root content="Delete the selected assets from this device">
              <Tooltip.Trigger>
                <Button
                  variant="danger"
                  size="small"
                  disabled={selected.size === 0}
                  onClick={() => setConfirming(true)}
                >
                  🗑 Delete
                </Button>
              </Tooltip.Trigger>
            </Tooltip.Root>
          </html.div>
        </html.div>
      ) : (
        <html.div style={s.row}>
          <html.span style={s.infoText}>
            {loading
              ? "Loading…"
              : `${display.length} asset${display.length === 1 ? "" : "s"}` +
                (orphanCount ? ` · ${orphanCount} unused` : "")}
          </html.span>
          {!pickTarget && (orphanCount > 0 || storedOrphans > 0) && (
            <Tooltip.Root content="Review and remove unused assets">
              <Tooltip.Trigger>
                <Button variant="danger" size="small" onClick={enterSelect}>
                  Remove {orphanCount} unused
                </Button>
              </Tooltip.Trigger>
            </Tooltip.Root>
          )}
        </html.div>
      )}

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        size="sm"
        title={`Delete ${selected.size} asset${selected.size === 1 ? "" : "s"}?`}
        cancelLabel="Cancel"
        confirmLabel="Delete"
        onConfirm={() => void deleteSelected()}
      >
        This removes {selected.size === 1 ? "it" : "them"} from this device and
        can't be undone.
      </ConfirmDialog>

      {armed && (
        <Callout variant="info" icon={false}>
          <html.div style={s.bannerRow}>
            <html.span>
              Choosing <html.strong>{armedKindLabel}</html.strong> for this
              block — click one below, or drop a file.
            </html.span>
            <html.div style={s.pushRight}>
              <Button variant="secondary" size="small" onClick={disarmPick}>
                Cancel
              </Button>
            </html.div>
          </html.div>
        </Callout>
      )}

      <FilterBar kind={kindFilter} locked={armed} onChange={setKindFilter} />

      <html.div style={s.scrollBody}>
        {!loading && display.length === 0 ? (
          <EmptyState message="No media yet. Drop files here — or upload on the canvas — and they will appear here." />
        ) : (
          <html.div style={s.grid}>
            {display.map((a) => (
              <AssetTile
                key={a.sha256}
                asset={a}
                siteId={siteId}
                published={synced.has(a.sha256)}
                pickMode={!!pickTarget}
                editable={!!ydoc && !pickTarget && !selectMode}
                selectMode={selectMode}
                checked={selected.has(a.sha256)}
                onToggle={() => toggleSelect(a.sha256)}
                onPick={() => pick(a.sha256, a)}
                onRename={(name) => rename(a, name)}
                onReplace={(file) => void onReplaceFile(a.sha256, file)}
                onRollback={(v) => rollback(a, v)}
                onDelete={() => void remove(a)}
              />
            ))}
          </html.div>
        )}
      </html.div>
    </html.div>
  );
}

/** The Media Library as a draggable, resizable, collapsible floating panel
 *  (Phase 2). Mirrors the Add/Format panel shells (position:fixed, grip header)
 *  but hosts the content browser. An Expand toggle pops it to a large centered
 *  overlay for heavy management; collapse hides the body to just the header. */
export function MediaLibraryPanel() {
  const { pickTarget, focusSeq } = useMediaLibrary();
  const armed = !!pickTarget;
  // Read membership directly: the expanded overlay below must not open while
  // this panel is docked (the group renders MediaLibraryBody instead), and
  // that decision happens before DockablePanel gets a chance to make it.
  const grouped = usePanelDock().members.includes("media");
  const [collapsed, setCollapsed] = useState(false);
  // Uncollapse whenever the panel is focused/armed (focusSeq bumps) — which is
  // why collapse is CONTROLLED here rather than left to DockablePanel.
  useEffect(() => {
    if (focusSeq > 0) setCollapsed(false);
  }, [focusSeq]);
  const [expanded, setExpanded] = useState(false);
  const [size, setSize] = useState({ w: 300, h: 480 });
  // Default BOTTOM-RIGHT (viewport/fixed coords), clear of the Add pane (top-left)
  // and the bottom-center zoom bar.
  const initial =
    typeof window !== "undefined"
      ? {
          x: Math.max(24, window.innerWidth - 300 - 24),
          y: Math.max(24, window.innerHeight - 480 - 24),
        }
      : { x: 24, y: 140 };
  const resizeRef = useRef<{
    x: number;
    y: number;
    w: number;
    h: number;
  } | null>(null);
  const resizeHandleRef = useRef<HTMLDivElement>(null);
  useNativeTitle(resizeHandleRef, "Resize");

  function onResizeDown(e: React.PointerEvent) {
    e.stopPropagation();
    resizeRef.current = { x: e.clientX, y: e.clientY, w: size.w, h: size.h };
    const move = (ev: PointerEvent) => {
      const r = resizeRef.current;
      if (!r) return;
      setSize({
        w: Math.max(240, r.w + (ev.clientX - r.x)),
        h: Math.max(260, r.h + (ev.clientY - r.y)),
      });
    };
    const up = () => {
      resizeRef.current = null;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }

  // Always mounted (like the Add pane) — no open/close. Docked into the tabbed
  // group → the group renders MediaLibraryBody instead.
  if (grouped) return null;

  // Expanded: a large centered overlay for heavy browse/manage. Deliberately
  // NOT a docked panel — it is a modal surface, so it bypasses the shell.
  if (expanded) {
    return (
      <html.div style={s.overlay} onClick={() => setExpanded(false)}>
        <html.div
          style={[s.sheet, armed && s.sheetArmed]}
          onClick={(e) => e.stopPropagation()}
        >
          <html.div style={s.expandHeader}>
            <html.span aria-hidden={true}>🗂️</html.span>
            <html.span style={s.expandTitle}>Media library</html.span>
            <Tooltip.Root content="Back to panel">
              <Tooltip.Trigger>
                <Button
                  variant="secondary"
                  size="small"
                  aria-label="Restore panel"
                  onClick={() => setExpanded(false)}
                >
                  ⤡
                </Button>
              </Tooltip.Trigger>
            </Tooltip.Root>
          </html.div>
          <MediaLibraryBody />
        </html.div>
      </html.div>
    );
  }

  return (
    <DockablePanel
      tab="media"
      title="Media"
      initial={initial}
      style={{ width: size.w, height: collapsed ? undefined : size.h }}
      armed={armed}
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      headerExtras={
        // Keep the press from starting a header drag.
        <html.span
          onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
        >
          <Tooltip.Root content="Expand">
            <Tooltip.Trigger>
              <Button
                variant="secondary"
                size="small"
                aria-label="Expand"
                onClick={() => setExpanded(true)}
              >
                ⤢
              </Button>
            </Tooltip.Trigger>
          </Tooltip.Root>
        </html.span>
      }
      footer={
        <html.div
          ref={resizeHandleRef}
          onPointerDown={onResizeDown}
          style={s.resizeHandle}
          aria-hidden={true}
        />
      }
    >
      <MediaLibraryBody />
    </DockablePanel>
  );
}

/** Segmented kind filter shared by browse (interactive) and pick (locked to the
 *  target kind, so it's visible WHY only one kind shows). */
function FilterBar({
  kind,
  locked,
  onChange,
}: {
  kind: FilterKind;
  locked: boolean;
  onChange: (k: FilterKind) => void;
}) {
  const opts: { k: FilterKind; label: string }[] = [
    { k: "all", label: "All" },
    { k: "image", label: "🖼 Images" },
    { k: "video", label: "🎬 Videos" },
    { k: "audio", label: "🎵 Audio" },
  ];
  return (
    <html.div style={s.filterBar}>
      <ToggleGroup
        aria-label="Filter by media type"
        size="small"
        value={[kind]}
        onValueChange={(v) => {
          // Single select: pressing the active kind again empties the value —
          // keep the current filter, as the old segmented tabs did.
          const next = opts.find((o) => o.k === v[0]);
          if (next) onChange(next.k);
        }}
      >
        {opts.map((o) => (
          <Toggle
            key={o.k}
            value={o.k}
            // Armed → only the target kind.
            disabled={locked && kind !== o.k}
          >
            {o.label}
          </Toggle>
        ))}
      </ToggleGroup>
    </html.div>
  );
}

function AssetTile({
  asset,
  siteId,
  published,
  pickMode,
  editable,
  selectMode,
  checked,
  onToggle,
  onPick,
  onRename,
  onReplace,
  onRollback,
  onDelete,
}: {
  asset: DisplayAsset;
  siteId: string | null;
  published: boolean;
  pickMode: boolean;
  editable: boolean;
  selectMode: boolean;
  checked: boolean;
  onToggle: () => void;
  onPick: () => void;
  onRename: (name: string) => void;
  onReplace: (file: File) => void;
  onRollback: (version: VersionRef) => void;
  onDelete: () => void;
}) {
  const status = useBackupStatus(asset.sha256);
  const roomPeers = useRoomPeerCount();
  const [holders, setHolders] = useState<number | null>(null);
  // Durability-depth: when a room is live, probe how many OTHER peers hold this
  // blob → "backed on N peers" / "needs a source". Solo → no probe → null →
  // falls back to the boolean chips. (plan §7 Q2)
  useEffect(() => {
    if (pickMode || !siteId || roomPeers === 0) {
      setHolders(null);
      return;
    }
    const probe = getCollabHolderProbe(siteId);
    if (!probe) return;
    let cancelled = false;
    void probe(asset.sha256).then((n) => {
      if (!cancelled) setHolders(n);
    });
    return () => {
      cancelled = true;
    };
  }, [asset.sha256, siteId, pickMode, roomPeers]);

  const chip = availabilityChip({
    status,
    published,
    stored: asset.stored,
    holders,
  });
  const orphan = asset.refCount === 0;
  const [name, setName] = useState(asset.name);
  const [showVersions, setShowVersions] = useState(false);
  useEffect(() => setName(asset.name), [asset.name]);
  const nameRef = useRef<HTMLInputElement>(null);
  const tileRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  useNativeTitle(
    tileRef,
    selectMode ? "Select to delete" : pickMode ? "Use this image" : undefined,
  );
  useNativeTitle(
    thumbRef,
    selectMode ? undefined : "Drag onto a page to add it",
  );
  // Block the browser's native image/link drag (which shows a globe ghost
  // and hijacks our pointer-based drag) so drop-to-fill + the image ghost
  // work.
  useNativeListener(thumbRef, "dragstart", (e) => e.preventDefault());
  // Web-only: `useAddDrag().start(info, pointerEvent)` exists on the web drag
  // context; the native variant uses `startAt(info, x, y)` instead and has no
  // `start`. This chrome is web-only, so reach `start` through a loose cast so
  // the shared file still typechecks on native (where it's simply absent → the
  // optional call no-ops).
  const drag = useAddDrag() as unknown as {
    start?: (info: Record<string, unknown>, e: React.PointerEvent) => void;
  };

  // Drag the thumbnail onto a page grid → create a widget of this asset's kind
  // bound to its sha (the same external-add pipeline the Add palette uses). The
  // ghost is the asset itself (previewUrl, read from the rendered thumb). A plain
  // click still picks (in pick mode); the drag threshold distinguishes them.
  const onThumbDown = (e: React.PointerEvent) => {
    if (e.button !== 0 || !drag.start) return;
    const el = e.currentTarget as HTMLElement;
    const media = el.querySelector("img, video") as
      | HTMLImageElement
      | HTMLVideoElement
      | null;
    const contentProps: Record<string, unknown> = { sha256: asset.sha256 };
    if (asset.mime) contentProps["mime"] = asset.mime;
    if (asset.ext) contentProps["ext"] = asset.ext;
    drag.start(
      {
        kind: "widget",
        templateId: asset.kind,
        icon:
          asset.kind === "video" ? "🎬" : asset.kind === "audio" ? "🎵" : "🖼️",
        label: asset.name || asset.kind,
        widgetType: asset.kind,
        contentProps,
        ...(media?.src ? { previewUrl: media.src } : {}),
      },
      e,
    );
  };

  return (
    <html.div
      ref={tileRef}
      style={[
        s.tile,
        (selectMode || pickMode) && s.pointer,
        checked && s.tileChecked,
      ]}
      onClick={selectMode ? onToggle : pickMode ? onPick : undefined}
    >
      <html.div
        ref={thumbRef}
        style={[s.thumbBox, selectMode ? s.pointer : s.grab]}
        // No drag-to-canvas while selecting.
        onPointerDown={selectMode ? undefined : onThumbDown}
      >
        <AssetThumb
          siteId={siteId}
          sha={asset.sha256}
          stored={asset.stored}
          kind={asset.kind}
        />
        {selectMode && (
          // The tile's click toggles too; stop the checkbox's own click so a
          // press on it toggles once.
          <html.span
            style={s.checkboxSlot}
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={checked}
              onChange={onToggle}
              aria-label={`Select ${asset.name || asset.kind}`}
            />
          </html.span>
        )}
        {asset.history.length > 0 && (
          <html.span style={s.versionBadge}>
            <Badge size="sm">v{asset.history.length + 1}</Badge>
          </html.span>
        )}
      </html.div>
      <html.div style={s.tileBody}>
        {editable ? (
          <Input
            ref={nameRef}
            aria-label="Asset name"
            value={name}
            placeholder="Untitled"
            onChange={(e) => setName(e.target.value)}
            onBlur={() => {
              // Read the value off the field, not the closure `name` — the
              // latter can lag a fast blur-after-type and silently drop the edit.
              const v = nameRef.current?.value ?? name;
              if (v !== asset.name) onRename(v);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
          />
        ) : (
          asset.name && <html.div style={s.assetName}>{asset.name}</html.div>
        )}
        <html.div style={s.metaRow}>
          <Badge size="sm" variant={chip.variant}>
            {chip.label}
          </Badge>
          <html.span style={s.useCount}>
            {orphan
              ? "unused"
              : `${asset.refCount} use${asset.refCount === 1 ? "" : "s"}`}
          </html.span>
        </html.div>

        {!pickMode && editable && (
          <html.div style={s.actions}>
            <FileTrigger
              accept={REPLACE_ACCEPT[asset.kind]}
              variant="secondary"
              size="small"
              onSelect={(files) => {
                const f = files[0];
                if (f) onReplace(f);
              }}
            >
              Replace
            </FileTrigger>
            {asset.history.length > 0 && (
              <Button
                variant="secondary"
                size="small"
                onClick={() => setShowVersions((v) => !v)}
              >
                {asset.history.length} version
                {asset.history.length === 1 ? "" : "s"}
              </Button>
            )}
            {orphan && (
              <Button variant="danger" size="small" onClick={onDelete}>
                Remove
              </Button>
            )}
          </html.div>
        )}

        {showVersions && asset.history.length > 0 && (
          <html.div style={s.versionStrip}>
            {[...asset.history].reverse().map((h) => (
              <VersionThumbButton
                key={h.sha}
                onClick={() => {
                  setShowVersions(false);
                  onRollback(h);
                }}
              >
                <AssetThumb
                  siteId={siteId}
                  sha={h.sha}
                  stored
                  kind={asset.kind}
                />
              </VersionThumbButton>
            ))}
          </html.div>
        )}
      </html.div>
    </html.div>
  );
}

/** A version's thumbnail as its own rollback button. Duro has no image/
 *  thumbnail button (a Button sizes and pads its label), so this stays an
 *  html.button. */
function VersionThumbButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  useNativeTitle(ref, "Roll back to this version");
  return (
    <html.button
      ref={ref}
      type="button"
      aria-label="Roll back to this version"
      onClick={onClick}
      style={s.versionThumbBtn}
    >
      {children}
    </html.button>
  );
}

function AssetThumb({
  siteId,
  sha,
  stored,
  kind = "image",
}: {
  siteId: string | null;
  sha: string;
  stored: boolean;
  kind?: AssetKind;
}) {
  // Audio has no visual poster — a static glyph placeholder (a separate leaf so
  // no bytes are fetched, and the hooks below stay unconditional).
  if (kind === "audio") return <AudioThumb />;
  return (
    <ImageOrVideoThumb siteId={siteId} sha={sha} stored={stored} kind={kind} />
  );
}

function AudioThumb() {
  return (
    <html.span aria-hidden={true} style={s.audioGlyph}>
      🎵
    </html.span>
  );
}

function ImageOrVideoThumb({
  siteId,
  sha,
  stored,
  kind,
}: {
  siteId: string | null;
  sha: string;
  stored: boolean;
  kind: "image" | "video";
}) {
  const [url, setUrl] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);
  // No local copy and no peer answered: say so instead of "resolving…" forever.
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!siteId) return;
    const revoke = () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
    const show = (blob: Blob | null) => {
      if (cancelled || !blob) return;
      const u = URL.createObjectURL(blob);
      urlRef.current = u;
      setUrl(u);
    };
    setMissing(false);
    // This device's copy, else a collab peer's; null when neither has it.
    void loadAssetBlob(siteId, sha).then((blob) => {
      if (cancelled) return;
      if (blob) show(blob);
      else setMissing(true);
    });
    return () => {
      cancelled = true;
      revoke();
    };
  }, [siteId, sha, stored]);

  if (!url)
    return stored ? null : (
      <html.span style={s.resolving}>
        {missing ? "not on this device" : "resolving…"}
      </html.span>
    );
  // Video: a muted, frozen first frame (preload metadata) — a lightweight poster.
  // react-strict-dom has no <video> (and Duro no media primitive), so the poster
  // is a raw element (the `allow` option in eslint.config.js).
  return kind === "video" ? (
    <video
      src={url}
      muted
      playsInline
      preload="metadata"
      draggable={false}
      {...css.props(s.fill)}
    />
  ) : (
    <html.img src={url} alt="" draggable={false} style={s.fill} />
  );
}

function availabilityChip({
  status,
  published,
  stored,
  holders,
}: {
  status: "backing-up" | "backed" | undefined;
  published: boolean;
  stored: boolean;
  holders: number | null;
}): { label: string; variant: BadgeVariant } {
  if (status === "backing-up")
    return { label: "backing up…", variant: "warning" };
  // Live probe result (a room is online): the precise durability-depth signal.
  if (holders !== null) {
    if (holders > 0)
      return {
        label: `backed on ${holders} peer${holders === 1 ? "" : "s"}`,
        variant: "success",
      };
    if (published) return { label: "published", variant: "info" };
    return { label: "only on this device", variant: "warning" };
  }
  // No probe (solo): the boolean fallbacks.
  if (published) return { label: "published", variant: "info" };
  if (status === "backed") return { label: "backed up", variant: "success" };
  if (stored) return { label: "on this device", variant: "default" };
  return { label: "needs a source", variant: "warning" };
}

// ── styles ──────────────────────────────────────────────────────────────────
const s = css.create({
  // The expanded (large overlay) surface.
  overlay: {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: layers.floating,
    backgroundColor: colors.scrim,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: spacing.lg,
  },
  sheet: {
    backgroundColor: colors.bgCard,
    borderRadius: radii.lg,
    width: "min(900px, 100%)",
    maxHeight: "min(700px, 90vh)",
    display: "flex",
    flexDirection: "column",
    boxShadow: shadows.lg,
    overflow: "hidden",
    position: "relative",
  },
  // While armed (choosing for a widget) the whole panel gets an accent ring so
  // the "you are picking" mode is unmistakable (mode-error guard).
  sheetArmed: {
    boxShadow: `0 0 0 ${borders.strong} ${colors.accent}, 0 0 0 ${microSpacing.px6} ${colors.infoBg}, ${shadows.lg}`,
  },
  // The expanded header — title + restore.
  expandHeader: {
    display: "flex",
    alignItems: "center",
    gap: spacing.sm,
    paddingTop: spacing.ms,
    paddingBottom: spacing.ms,
    paddingLeft: spacing.md,
    paddingRight: spacing.md,
    borderBottomWidth: borders.hairline,
    borderBottomStyle: "solid",
    borderBottomColor: colors.border,
    fontSize: typography.fontSizeMd,
    color: colors.text,
  },
  expandTitle: {
    fontWeight: typography.fontWeightBold,
    flexGrow: 1,
  },
  // The Media Library CONTENT wrapper (fills the shell body, scrolls inside).
  bodyRoot: {
    position: "relative",
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: "0%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  // The count/sweep row and the selection bar share one shape.
  row: {
    display: "flex",
    alignItems: "center",
    gap: spacing.sm,
    paddingTop: spacing.sm,
    paddingBottom: spacing.sm,
    paddingLeft: spacing.md,
    paddingRight: spacing.md,
    borderBottomWidth: borders.hairline,
    borderBottomStyle: "solid",
    borderBottomColor: colors.border,
  },
  selectBar: {
    backgroundColor: colors.bgCard,
  },
  selectedCount: {
    fontWeight: typography.fontWeightSemibold,
    color: colors.text,
    fontSize: typeScale.fontSize2,
  },
  infoText: {
    fontSize: typography.fontSizeXs,
    color: colors.textMuted,
  },
  // Pins its contents right without flex-grow.
  pushRight: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: spacing.sm,
    flexShrink: 0,
  },
  bannerRow: {
    display: "flex",
    alignItems: "center",
    gap: spacing.ms,
  },
  scrollBody: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: "0%",
    minHeight: 0,
    overflowY: "auto",
    padding: spacing.md,
  },
  resizeHandle: {
    position: "absolute",
    right: 0,
    bottom: 0,
    width: sizes.handle,
    height: sizes.handle,
    cursor: "nwse-resize",
    // A subtle corner grip.
    backgroundImage: `linear-gradient(135deg, transparent 0 50%, ${colors.border} 50% 60%, transparent 60% 70%, ${colors.border} 70% 80%, transparent 80%)`,
  },
  // Transient "✓ Added" confirmation after a pick binds.
  toast: {
    position: "absolute",
    top: spacing.sm,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: layers.floating,
    boxShadow: shadows.md,
    borderRadius: radii.full,
  },
  // Segmented kind filter.
  filterBar: {
    paddingTop: spacing.sm,
    paddingBottom: spacing.sm,
    paddingLeft: spacing.md,
    paddingRight: spacing.md,
    borderBottomWidth: borders.hairline,
    borderBottomStyle: "solid",
    borderBottomColor: colors.border,
  },
  // Shown while dragging files over the dialog. pointerEvents:none so the drop
  // still lands on the body's drop listener, not this overlay.
  dropOverlay: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: layers.raised,
    backgroundColor: colors.infoBg,
    borderWidth: borders.strong,
    borderStyle: "dashed",
    borderColor: colors.accent,
    borderRadius: radii.lg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
  },
  dropHint: {
    backgroundColor: colors.accent,
    color: colors.accentContrast,
    fontSize: typography.fontSizeSm,
    fontWeight: typography.fontWeightBold,
    paddingTop: spacing.sm,
    paddingBottom: spacing.sm,
    paddingLeft: spacing.md,
    paddingRight: spacing.md,
    borderRadius: radii.full,
    boxShadow: shadows.md,
  },
  grid: {
    display: "grid",
    // ~2 columns in the narrow docked panel (≈264–300px); more as it's resized
    // or expanded (auto-fill).
    gridTemplateColumns: `repeat(auto-fill, minmax(${sizes.mediaTileMinW}, 1fr))`,
    gap: spacing.ms,
  },
  tile: {
    borderWidth: borders.hairline,
    borderStyle: "solid",
    borderColor: colors.border,
    borderRadius: radii.md,
    overflow: "hidden",
    backgroundColor: colors.bgCard,
    position: "relative",
    cursor: "default",
  },
  tileChecked: {
    outlineWidth: borders.strong,
    outlineStyle: "solid",
    outlineColor: colors.accent,
    outlineOffset: `calc(-1 * ${borders.strong})`,
  },
  pointer: { cursor: "pointer" },
  grab: { cursor: "grab" },
  thumbBox: {
    aspectRatio: 4 / 3,
    position: "relative",
    backgroundImage: `repeating-conic-gradient(${colors.bgCardHover} 0% 25%, ${colors.bgCard} 0% 50%)`,
    backgroundPosition: "50%",
    backgroundSize: `${spacing.md} ${spacing.md}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    touchAction: "none",
  },
  fill: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  checkboxSlot: {
    position: "absolute",
    top: microSpacing.px6,
    left: microSpacing.px6,
  },
  versionBadge: {
    position: "absolute",
    top: microSpacing.px6,
    right: microSpacing.px6,
  },
  tileBody: {
    paddingTop: spacing.sm,
    paddingBottom: spacing.sm,
    paddingLeft: spacing.sm,
    paddingRight: spacing.sm,
  },
  assetName: {
    fontSize: typeScale.fontSize2,
    fontWeight: typography.fontWeightSemibold,
    color: colors.text,
  },
  metaRow: {
    display: "flex",
    alignItems: "center",
    gap: microSpacing.px6,
    justifyContent: "space-between",
    marginTop: spacing.xs,
  },
  useCount: {
    fontSize: typography.fontSizeXs,
    color: colors.textMuted,
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: microSpacing.px6,
    marginTop: spacing.sm,
  },
  versionStrip: {
    display: "flex",
    gap: microSpacing.px6,
    marginTop: spacing.sm,
    overflowX: "auto",
  },
  versionThumbBtn: {
    flexShrink: 0,
    width: sizes.swatchW,
    height: sizes.swatchH,
    padding: 0,
    borderWidth: borders.hairline,
    borderStyle: "solid",
    borderColor: colors.border,
    borderRadius: radii.px6,
    overflow: "hidden",
    cursor: "pointer",
    backgroundColor: colors.bgCard,
  },
  audioGlyph: {
    fontSize: typeScale.fontSize7,
  },
  resolving: {
    fontSize: typography.fontSizeXs,
    color: colors.textMuted,
  },
});
